import type {
  PatchProjectVariablesRequestBody,
  PatchProjectVariablesResponseBody,
} from "@imagine/ai-service/shared-types/route-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { imagineStore } from "@/stores/imagine.store";
import { aiServiceFetch } from "@/lib/ai-service-client";
import { HttpError } from "@/lib/http-error";
import { trackAnalyticsEvent } from "@/lib/analytics";

export function useSaveProjectVariablesMutation({
  projectId,
  onSuccess,
  onError,
}: {
  projectId: string | null;
  onSuccess?: (data: PatchProjectVariablesResponseBody) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();
  const refreshIframe = imagineStore.use.previewState.refreshIframe();

  return useMutation<
    PatchProjectVariablesResponseBody,
    Error,
    PatchProjectVariablesRequestBody
  >({
    mutationFn: async (params) => {
      const response = await aiServiceFetch(
        `/api/projects/${projectId}/variables`,
        {
          method: "PATCH",
          body: JSON.stringify(params),
        },
      );

      if (!response.ok) {
        throw await HttpError.fromResponse(response);
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["environment-variables", projectId],
      });
      refreshIframe();
      if (projectId) {
        void trackAnalyticsEvent("project.variables_updated", { projectId });
      }
      onSuccess?.(data);
    },
    onError,
  });
}
